import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportsSection from '../components/ReportsSection';
import CountryMultiSelect from '../components/CountryMultiSelect';
import VisaApplicationFilters from '../components/VisaApplicationFilters';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import ManagerFilters from '../components/ManagerFilters';
import ActionsMenuButton from '../components/ActionsMenuButton';
import {
  DEFAULT_VISA_FILTERS,
  ERROR_POOL_FILTERS,
  FINALIZE_POOL_FILTERS,
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import {
  DEFAULT_MANAGER_FILTERS,
  filterManagers,
} from '../utils/managerFilters';
import {
  parseCountryIds,
  serializeCountryIds,
  getCountryDisplay,
} from '../utils/countryUtils';
import useCountries from '../hooks/useCountries';
import { formatAirlineAmount, parseCurrencyCode } from '../utils/currencyUtils';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';
import VisaNotesSection from '../components/VisaNotesSection';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/airlines-admin/dashboard': 'dashboard',
  '/airlines-admin/visa-applications': 'visa_applications',
  '/airlines-admin/error-pool': 'error_pool',
  '/airlines-admin/finalize-pool': 'finalize_pool',
  '/airlines-admin/managers': 'managers',
  '/airlines-admin/profile': 'profile',
  '/airlines-admin/billing': 'billing',
  '/airlines-admin/reports': 'reports',
  '/airlines-admin/api-integration': 'api_integration',
};

const formatTransactionType = (type) => {
  if (!type) return '-';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const isCreditTransaction = (type) => type === 'top_up';

const AirlinesAdminDashboard = () => {
  const { updateUser, user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeReturnHandled = useRef(false);
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [subTab, setSubTab] = useState('top_up');
  const [profile, setProfile] = useState(null);
  const [managers, setManagers] = useState([]);
  const [topUps, setTopUps] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { countries, loading: fetchingCountries } = useCountries();
  const [newManager, setNewManager] = useState({ name: '', email: '', password: '', from_country: [], to_country: [] });
  const [editManager, setEditManager] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone_number: '', country: '', currency: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showManagerForm, setShowManagerForm] = useState(false);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [errorPoolFilters, setErrorPoolFilters] = useState(ERROR_POOL_FILTERS);
  const [finalizePoolFilters, setFinalizePoolFilters] = useState(FINALIZE_POOL_FILTERS);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [managerFilters, setManagerFilters] = useState(DEFAULT_MANAGER_FILTERS);
  const [managerActionMenu, setManagerActionMenu] = useState(null);
  const [apiKeyInfo, setApiKeyInfo] = useState({ hasApiKey: false, apiKey: null, createdAt: null });
  const [apiKeyLoading, setApiKeyLoading] = useState(false);

  const filteredVisaApplications = useMemo(
    () => filterVisaApplications(visaApplications, visaFilters),
    [visaApplications, visaFilters]
  );

  const filteredManagers = useMemo(
    () => filterManagers(managers, managerFilters),
    [managers, managerFilters]
  );

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    setActiveTab(tab);
    handleTabData(tab);
  }, [location.pathname]);

  useEffect(() => {
    const topup = searchParams.get('topup');
    const sessionId = searchParams.get('session_id');

    if (topup === 'cancelled') {
      setError('Stripe payment was cancelled.');
      setSearchParams({}, { replace: true });
      return;
    }

    if (topup === 'success' && sessionId && !stripeReturnHandled.current) {
      stripeReturnHandled.current = true;
      setSubTab('top_up');
      confirmStripePayment(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!managerActionMenu) return;

    const closeMenu = () => setManagerActionMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('resize', closeMenu);
    };
  }, [managerActionMenu]);

  const handleTabData = (tabId) => {
    if (tabId === 'managers') fetchManagers();
    if (tabId === 'billing') { fetchTopUps(); fetchTransactions(); }
    if (tabId === 'visa_applications') { fetchVisaApplications(); fetchManagers(); }
    if (tabId === 'error_pool' || tabId === 'finalize_pool') { fetchVisaApplications(); fetchManagers(); }
    if (tabId === 'reports') fetchReports();
    if (tabId === 'api_integration') fetchApiKeyInfo();
    if (tabId === 'dashboard') fetchAllData();
  };

  const fetchReports = async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/airlines-admin/reports`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [dashRes] = await Promise.all([
        axios.get(`${API_URL}/airlines-admin/dashboard`)
      ]);
      setProfile(dashRes.data.profile);
      setStats(dashRes.data);
      setProfileForm({
        name: dashRes.data.profile?.name || '',
        phone_number: dashRes.data.profile?.phone_number || '',
        country: dashRes.data.profile?.country || '',
        currency: dashRes.data.profile?.currency || ''
      });
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API_URL}/airlines-admin/managers`);
      setManagers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchTopUps = async () => {
    try {
      const res = await axios.get(`${API_URL}/airlines-admin/top-ups`);
      setTopUps(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchVisaApplications = async () => {
    try {
      const res = await axios.get(`${API_URL}/visa-applications`);
      setVisaApplications(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load visa applications');
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API_URL}/airlines-admin/transactions`);
      setTransactions(res.data);
    } catch (err) { console.error(err); }
  };

  // ==================== MANAGERS ====================

  const handleAddManager = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newManager.name || !newManager.email || !newManager.password) {
      setError('Name, email and password are required'); return;
    }
    try {
      await axios.post(`${API_URL}/airlines-admin/managers`, {
        ...newManager,
        from_country: serializeCountryIds(newManager.from_country),
        to_country: serializeCountryIds(newManager.to_country),
      });
      setSuccess('Manager added successfully!');
      setNewManager({ name: '', email: '', password: '', from_country: [], to_country: [] });
      fetchManagers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add manager');
    }
  };

  const handleEditManager = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await axios.put(`${API_URL}/airlines-admin/managers/${editManager.id}`, {
        ...editManager,
        from_country: serializeCountryIds(editManager.from_country),
        to_country: serializeCountryIds(editManager.to_country),
      });
      setSuccess('Manager updated successfully!');
      setEditManager(null);
      fetchManagers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update manager');
    }
  };

  const handleDeleteManager = async (id) => {
    if (!window.confirm('Are you sure you want to delete this manager?')) return;
    try {
      await axios.delete(`${API_URL}/airlines-admin/managers/${id}`);
      setSuccess('Manager deleted successfully');
      fetchManagers();
    } catch (err) {
      setError('Failed to delete manager');
    }
  };

  const toggleManagerStatus = async (id) => {
    try {
      await axios.put(`${API_URL}/airlines-admin/managers/${id}/toggle-status`);
      fetchManagers();
    } catch (err) {
      console.error(err);
    }
  };

  // ==================== TOP UP ====================

  const confirmStripePayment = async (sessionId) => {
    setTopUpLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/airlines-admin/stripe/confirm-session`, { sessionId });
      setSuccess(res.data.message);
      setTopUpAmount('');
      fetchTopUps();
      fetchTransactions();
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm payment');
    } finally {
      setTopUpLoading(false);
      setSearchParams({}, { replace: true });
    }
  };

  const handleTopUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!topUpAmount || parseFloat(topUpAmount) < 1) {
      setError('Please enter an amount of at least $1.00');
      return;
    }
    setTopUpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/airlines-admin/stripe/create-checkout-session`, {
        amount: parseFloat(topUpAmount),
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Stripe payment');
      setTopUpLoading(false);
    }
  };

  const formatPaymentMethod = (method) => {
    if (method === 'stripe') return 'Stripe (Card)';
    if (method === 'free_credit') return 'Free Credit';
    if (!method) return 'Bank Transfer';
    return method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // ==================== API KEY ====================

  const fetchApiKeyInfo = async () => {
    setApiKeyLoading(true);
    try {
      const res = await axios.get(`${API_URL}/airlines-admin/api-key`);
      setApiKeyInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API key info');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    if (apiKeyInfo.hasApiKey && !window.confirm('Generating a new key will revoke your existing key. Continue?')) {
      return;
    }
    setError('');
    setSuccess('');
    setApiKeyLoading(true);
    try {
      const res = await axios.post(`${API_URL}/airlines-admin/api-key/generate`);
      setApiKeyInfo({
        hasApiKey: true,
        apiKey: res.data.apiKey,
        createdAt: new Date().toISOString(),
      });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!window.confirm('Revoke your API key? External integrations will stop working until you generate a new key.')) {
      return;
    }
    setError('');
    setSuccess('');
    setApiKeyLoading(true);
    try {
      const res = await axios.delete(`${API_URL}/airlines-admin/api-key`);
      setApiKeyInfo({ hasApiKey: false, apiKey: null, createdAt: null });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!apiKeyInfo.apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKeyInfo.apiKey);
      setSuccess('API key copied to clipboard');
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  // ==================== PROFILE ====================

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await axios.put(`${API_URL}/airlines-admin/profile`, profileForm);
      updateUser({ name: profileForm.name });
      setSuccess('Profile updated successfully!');
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Passwords do not match'); return;
    }
    if (passwordForm.new_password.length < 6) {
      setError('New password must be at least 6 characters'); return;
    }
    try {
      await axios.put(`${API_URL}/airlines-admin/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setSuccess('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner text="Loading dashboard..." />
      </Layout>
    );
  }

  const airlineCurrency = profile?.currency || 'USD';
  const airlineCurrencyCode = parseCurrencyCode(airlineCurrency) || 'USD';
  const formatMoney = (amount) => formatAirlineAmount(amount, airlineCurrency);
  const topUpMin = airlineCurrencyCode === 'JPY' ? 100 : 1;
  const topUpStep = airlineCurrencyCode === 'JPY' ? '1' : '0.01';

  // ==================== DASHBOARD ====================

  const renderDashboard = () => (
    <div>
      <div className="mb-8">
        <h1 className="primary-title">Welcome, {profile?.name || 'Admin'}!</h1>
        <p className="text-ink-secondary mt-1">Airline Management Dashboard</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Country</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{profile?.country_name || profile?.country || 'N/A'}</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Currency</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{profile?.currency || 'N/A'}</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Managers</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{stats?.totalManagers || 0}</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Visa Applications</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{stats?.totalVisaApplications || 0}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Total Top Up</p>
          <p className="text-2xl font-semibold text-ink mt-1">{formatMoney(stats?.totalTopUp || 0)}</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Balance</p>
          <p className="text-2xl font-semibold text-ink mt-1">{formatMoney(stats?.balance || 0)}</p>
        </div>
      </div>
    </div>
  );
  const renderApplicationDetailModal = () => {
    if (!selectedApplication) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedApplication(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
            <button type="button" onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="font-medium text-gray-700">Passenger ID:</span> {selectedApplication.passenger_id}</div>
            <div><span className="font-medium text-gray-700">From:</span> {selectedApplication.from_country_name || selectedApplication.from_country}</div>
            <div><span className="font-medium text-gray-700">To:</span> {selectedApplication.to_country_name || selectedApplication.to_country}</div>
            <div><span className="font-medium text-gray-700">Visa Type:</span> {selectedApplication.visa_type}</div>
            <div><span className="font-medium text-gray-700">Manager:</span> {selectedApplication.manager_name || '-'}</div>
            <div><span className="font-medium text-gray-700">Application Status:</span> {formatStatusLabel(selectedApplication.application_status)}</div>
            {selectedApplication.status_description && (
              <div><span className="font-medium text-gray-700">Status Description:</span> {selectedApplication.status_description}</div>
            )}
            <VisaNotesSection application={selectedApplication} role={user?.role} />
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Data</p>
              <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedApplication.customer_visa_application_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorPool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Error Pool"
        subtitle="Customer visa applications with error status for your airline"
        applications={visaApplications}
        filters={errorPoolFilters}
        onFiltersChange={setErrorPoolFilters}
        onClearFilters={() => setErrorPoolFilters(ERROR_POOL_FILTERS)}
        showManagerFilter
        managerOptions={managers}
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No error applications in your pool."
      />
    </>
  );

  const renderFinalizePool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Finalize Pool"
        subtitle="Finalized customer visa applications for your airline"
        applications={visaApplications}
        filters={finalizePoolFilters}
        onFiltersChange={setFinalizePoolFilters}
        onClearFilters={() => setFinalizePoolFilters(FINALIZE_POOL_FILTERS)}
        showManagerFilter
        managerOptions={managers}
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No finalized applications in your pool."
      />
    </>
  );

  // ==================== VISA APPLICATIONS TAB ====================

  const renderVisaApplications = () => {
    const detailModal = selectedApplication ? (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedApplication(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
            <button type="button" onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="font-medium text-gray-700">Passenger ID:</span> {selectedApplication.passenger_id}</div>
            <div><span className="font-medium text-gray-700">From:</span> {selectedApplication.from_country_name || selectedApplication.from_country}</div>
            <div><span className="font-medium text-gray-700">To:</span> {selectedApplication.to_country_name || selectedApplication.to_country}</div>
            <div><span className="font-medium text-gray-700">Visa Type:</span> {selectedApplication.visa_type}</div>
            <div><span className="font-medium text-gray-700">Manager:</span> {selectedApplication.manager_name || '-'}</div>
            <div><span className="font-medium text-gray-700">Application Status:</span> {formatStatusLabel(selectedApplication.application_status)}</div>
            {selectedApplication.status_description && (
              <div><span className="font-medium text-gray-700">Status Description:</span> {selectedApplication.status_description}</div>
            )}
            <VisaNotesSection application={selectedApplication} role={user?.role} />
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Data</p>
              <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedApplication.customer_visa_application_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <div>
        <div className="mb-8">
          <h1 className="primary-title">Visa Applications</h1>
          <p className="text-gray-500 mt-1">Customer visa applications for your airline</p>
        </div>

        {detailModal}

        <VisaApplicationFilters
          applications={visaApplications}
          filters={visaFilters}
          onChange={setVisaFilters}
          onClear={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
          showManagerFilter
          managerOptions={managers}
          totalCount={visaApplications.length}
          filteredCount={filteredVisaApplications.length}
        />

        <div className="table-shell">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Visa Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Application Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-400">No visa applications yet.</td>
                  </tr>
                ) : filteredVisaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-400">No applications match your filters.</td>
                  </tr>
                ) : (
                  filteredVisaApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{getApplicantName(app.customer_visa_application_data)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => setHistoryApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MANAGERS TAB ====================

  const getMainElement = () => document.querySelector('main');

  const toggleManagerActionMenu = (event, manager) => {
    event.stopPropagation();

    if (managerActionMenu?.manager.id === manager.id) {
      setManagerActionMenu(null);
      return;
    }

    const mainEl = getMainElement();
    if (!mainEl) return;

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const mainRect = mainEl.getBoundingClientRect();
    setManagerActionMenu({
      manager,
      top: buttonRect.bottom - mainRect.top + mainEl.scrollTop + 4,
      left: Math.max(8, buttonRect.right - mainRect.left + mainEl.scrollLeft - 176),
    });
  };

  const renderManagerActionMenu = () => {
    if (!managerActionMenu) return null;

    const mainEl = getMainElement();
    if (!mainEl) return null;

    const { manager, top, left } = managerActionMenu;

    return createPortal(
      <div
        className="absolute z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setEditManager({
              ...manager,
              password: '',
              from_country: parseCountryIds(manager.from_country),
              to_country: parseCountryIds(manager.to_country),
            });
            setManagerActionMenu(null);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => {
            toggleManagerStatus(manager.id);
            setManagerActionMenu(null);
          }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
            manager.status === 'active'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {manager.status === 'active' ? 'Deactivate' : 'Activate'}
        </button>
        <button
          type="button"
          onClick={() => {
            setManagerActionMenu(null);
            handleDeleteManager(manager.id);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Delete
        </button>
      </div>,
      mainEl
    );
  };

  const renderManagers = () => {
    const modal = editManager ? (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditManager(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Manager</h3>
          <form onSubmit={handleEditManager} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={editManager.name} onChange={e => setEditManager({...editManager, name: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={editManager.email} onChange={e => setEditManager({...editManager, email: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                <input type="password" value={editManager.password || ''} onChange={e => setEditManager({...editManager, password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" placeholder="Leave blank to keep current" />
              </div>
              <CountryMultiSelect
                label="From Country"
                value={parseCountryIds(editManager.from_country)}
                onChange={(selected) => setEditManager((prev) => ({ ...prev, from_country: selected }))}
                countries={countries}
                loading={fetchingCountries}
                placeholder="Click to select from countries"
              />
              <CountryMultiSelect
                label="To Country"
                value={parseCountryIds(editManager.to_country)}
                onChange={(selected) => setEditManager((prev) => ({ ...prev, to_country: selected }))}
                countries={countries}
                loading={fetchingCountries}
                placeholder="Click to select to countries"
              />
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button type="button" onClick={() => setEditManager(null)} className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">Update Manager</button>
            </div>
          </form>
        </div>
      </div>
    ) : null;
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Managers</h2>
          <button onClick={() => setShowManagerForm(!showManagerForm)} className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">
            {showManagerForm ? 'Cancel' : '+ Add Manager'}
          </button>
        </div>
        {showManagerForm && (
          <form onSubmit={handleAddManager} className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="font-medium text-gray-900 mb-4">Add New Manager</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={newManager.name} onChange={e => setNewManager({...newManager, name: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" placeholder="Manager name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={newManager.email} onChange={e => setNewManager({...newManager, email: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" placeholder="manager@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input type="password" value={newManager.password} onChange={e => setNewManager({...newManager, password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" placeholder="Set password" />
              </div>
              <CountryMultiSelect
                label="From Country"
                value={newManager.from_country}
                onChange={(selected) => setNewManager((prev) => ({ ...prev, from_country: selected }))}
                countries={countries}
                loading={fetchingCountries}
                placeholder="Click to select from countries"
              />
              <CountryMultiSelect
                label="To Country"
                value={newManager.to_country}
                onChange={(selected) => setNewManager((prev) => ({ ...prev, to_country: selected }))}
                countries={countries}
                loading={fetchingCountries}
                placeholder="Click to select to countries"
              />
            </div>
            <button type="submit" className="mt-4 px-6 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">Add Manager</button>
          </form>
        )}
        {modal}
        <ManagerFilters
          managers={managers}
          countries={countries}
          filters={managerFilters}
          onChange={setManagerFilters}
          onClear={() => setManagerFilters(DEFAULT_MANAGER_FILTERS)}
          totalCount={managers.length}
          filteredCount={filteredManagers.length}
        />
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {managers.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-8 text-gray-400">No managers yet.</td></tr>
                ) : filteredManagers.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-8 text-gray-400">No managers match your filters.</td></tr>
                ) : filteredManagers.map(m => (
                  <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{m.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{m.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{m.from_country_name || getCountryDisplay(m, 'from_country', countries)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{m.to_country_name || getCountryDisplay(m, 'to_country', countries)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{m.status}</span>
                    </td>
                    <td className="py-3 px-4">
                      <ActionsMenuButton
                        label={`Actions for ${m.name}`}
                        expanded={managerActionMenu?.manager.id === m.id}
                        onClick={(e) => toggleManagerActionMenu(e, m)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };
  // ==================== API INTEGRATION TAB ====================

  const renderApiIntegration = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">API Integration</h2>
      <p className="text-gray-500 mb-6">
        Generate an API key to submit visa applications from your systems.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your API Key</h3>

          {apiKeyLoading && !apiKeyInfo.hasApiKey ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : (
            <div className="space-y-4">
              {apiKeyInfo.hasApiKey ? (
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                  <p className="text-sm text-gray-500 mb-2">Your API key</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={apiKeyInfo.apiKey || ''}
                      className="flex-1 font-mono text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
                    />
                    <button
                      type="button"
                      onClick={handleCopyApiKey}
                      className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-hover"
                    >
                      Copy
                    </button>
                  </div>
                  {apiKeyInfo.createdAt && (
                    <p className="text-xs text-gray-400 mt-2">
                      Created {new Date(apiKeyInfo.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No API key yet. Generate one to connect your systems.
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleGenerateApiKey}
                  disabled={apiKeyLoading}
                  className="px-5 py-2.5 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors disabled:opacity-50"
                >
                  {apiKeyInfo.hasApiKey ? 'Regenerate API Key' : 'Generate API Key'}
                </button>
                {apiKeyInfo.hasApiKey && (
                  <button
                    type="button"
                    onClick={handleRevokeApiKey}
                    disabled={apiKeyLoading}
                    className="px-5 py-2.5 text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50"
                  >
                    Revoke Key
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Visa Application</h3>
          <p className="text-sm text-gray-600 mb-4">
            POST passenger data to the endpoint below. Include your API key in the{' '}
            <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">X-API-Key</code> header
            or as <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">api_key</code> in the body.
          </p>
          <div className="rounded-xl bg-ink text-gray-100 p-4 text-xs font-mono overflow-x-auto whitespace-pre">
{`POST ${API_URL}/visa-applications
X-API-Key: ${apiKeyInfo.apiKey || 'vfa_your_api_key_here'}
Content-Type: application/json

{
  "passenger_id": "PAX-12345",
  "from_country_id": "1",
  "to_country_id": "3",
  "visa_type": "tourist",
  "customer_visa_application_data": {
    "first_name": "John",
    "last_name": "Doe",
    "passport_number": "AB1234567"
  }
}`}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            The airline is identified automatically from your API key — do not send airline_id.
          </p>
        </div>
      </div>
    </div>
  );

  // ==================== PROFILE TAB ====================

  const renderProfile = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Settings</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Airline Information</h3>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input type="text" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={profile?.email || ''} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 cursor-not-allowed" disabled />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input type="text" value={profileForm.phone_number} onChange={e => setProfileForm({...profileForm, phone_number: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={profileForm.country}
                onChange={(e) => {
                  const selectedCountry = countries.find((c) => String(c.id) === String(e.target.value));
                  setProfileForm({
                    ...profileForm,
                    country: e.target.value,
                    currency: selectedCountry?.currency || profileForm.currency,
                  });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                disabled={fetchingCountries}
              >
                <option value="">{fetchingCountries ? 'Loading countries...' : 'Select country'}</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input type="text" value={profileForm.currency} onChange={e => setProfileForm({...profileForm, currency: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IATA Number</label>
              <input type="text" value={profile?.iata_number || ''} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 cursor-not-allowed" disabled />
            </div>
            <button type="submit" className="px-6 py-2.5 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">Update Profile</button>
          </form>
        </div>
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm({...passwordForm, current_password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required placeholder="Min. 6 characters" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" required />
            </div>
            <button type="submit" className="px-6 py-2.5 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">Change Password</button>
          </form>
        </div>
      </div>
    </div>
  );
  // ==================== BILLING TAB ====================

  const renderBilling = () => (
    <div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setSubTab('top_up')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === 'top_up' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Top Up</button>
        <button onClick={() => setSubTab('passbook')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === 'passbook' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Passbook</button>
      </div>

      {subTab === 'top_up' && (
        <div>
          <div className="card p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Up Your Account</h3>
            <p className="text-sm text-gray-500 mb-4">
              Payments are processed in your registered currency: <strong>{airlineCurrency}</strong>
            </p>
            <form onSubmit={handleTopUp} className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ({airlineCurrencyCode})
                </label>
                <input
                  type="number"
                  step={topUpStep}
                  min={topUpMin}
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder="0.00"
                  required
                  disabled={topUpLoading}
                />
              </div>
              <button
                type="submit"
                disabled={topUpLoading}
                className="px-6 py-2.5 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {topUpLoading ? 'Processing...' : 'Pay with Stripe'}
              </button>
            </form>
            <p className="text-xs text-gray-400 mt-2">
              Secure card payment via Stripe. Test card: 4242 4242 4242 4242, any future expiry, any CVC.
            </p>
          </div>
          <div className="table-shell">
            <h3 className="text-lg font-semibold text-gray-900 p-6 pb-0">Top Up History</h3>
            <div className="overflow-x-auto p-6 pt-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Payment Method</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topUps.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-8 text-gray-400">No top ups yet.</td></tr>
                  ) : topUps.map(t => (
                    <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-600">{new Date(t.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{formatMoney(t.amount)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatPaymentMethod(t.payment_method)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          t.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          t.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                          'bg-red-100 text-red-700'
                        }`}>{t.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {subTab === 'passbook' && (
        <div className="table-shell">
          <h3 className="text-lg font-semibold text-gray-900 p-6 pb-0">Transaction History</h3>
          <div className="overflow-x-auto p-6 pt-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Description</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-500">Amount</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-500">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-8 text-gray-400">No transactions yet.</td></tr>
                ) : transactions.map(tx => (
                  <tr key={tx.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(tx.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isCreditTransaction(tx.type) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{formatTransactionType(tx.type)}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{tx.description || '-'}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 text-right">{formatMoney(tx.amount)}</td>
                    <td className="py-3 px-4 text-sm text-right">{formatMoney(tx.balance_after ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
  // ==================== MESSAGES ====================

  const renderMessages = () => {
    return (
      <div>
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            {success}
          </div>
        )}
      </div>
    );
  };

  const renderReports = () => (
    <ReportsSection
      title="Airline Reports"
      subtitle="Complete analytics for your visa applications, routes, and account"
      report={report}
      loading={reportLoading}
    />
  );

  // ==================== MAIN RENDER ====================

  return (
    <Layout>
      {renderManagerActionMenu()}
      <div>
        {renderMessages()}

        {/* Tab Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'visa_applications' && renderVisaApplications()}
        {activeTab === 'error_pool' && renderErrorPool()}
        {activeTab === 'finalize_pool' && renderFinalizePool()}
        {activeTab === 'managers' && renderManagers()}
        {activeTab === 'profile' && renderProfile()}
        {activeTab === 'api_integration' && renderApiIntegration()}
        {activeTab === 'billing' && renderBilling()}
        {activeTab === 'reports' && renderReports()}

        {historyApplication && (
          <VisaApplicationHistoryModal
            application={historyApplication}
            historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
            onClose={() => setHistoryApplication(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default AirlinesAdminDashboard;
